import type { ComponentType, ReactNode } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Icon } from "../Icons/Icon";
import { RadioMark } from "./RadioMark";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface ChoiceRowOption {
  value: string;
  /**
   * A glyph for the thing being picked, shown in a round chip on the leading
   * edge. Omit it and the row starts with its content.
   */
  icon?: ComponentType<{ className?: string }> | undefined;
  /**
   * Whatever names the choice — a label, a tag beside it, a line of detail
   * underneath. The row owns the frame, the chip, the mark and the keyboard
   * handling; what goes between them is yours.
   */
  content: ReactNode;
  /** Shown but unpickable. Prefer this over hiding a choice, so the reader
   *  still learns it exists. */
  disabled?: boolean | undefined;
}

interface ChoiceRowsProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: ChoiceRowOption[];
  /**
   * Cap the list in px and scroll past it. Set this when the list is as long as
   * the account happens to make it — a dozen organizations shouldn't push the
   * dialog's buttons off screen. Omit it and the list grows to fit.
   */
  maxHeight?: number | undefined;
  "data-testid"?: string | undefined;
}

/**
 * Pick one of a list of things, each a full-width row you click anywhere on.
 *
 * The sibling of `ChoiceCards`, for the case that isn't a handful of choices
 * arguing for themselves: an open-ended list where every entry has the same
 * shape and the set could be two or twenty. So the rows go full width and stay
 * stacked, they carry a radio mark rather than relying on the tint alone (with
 * ten rows on screen, "which one is blue" is a worse question than "which one
 * is marked"), and the list can cap its own height and scroll.
 *
 * Single-select, like `ChoiceCards` — both are radio groups. For picking
 * several things out of a list, that's a column of `Checkbox`.
 */
export function ChoiceRows({
  value,
  onChange,
  options,
  maxHeight,
  "data-testid": dataTestid,
}: ChoiceRowsProps) {
  const markDirty = useMarkDialogDirty();

  return (
    <RadioGroupPrimitive.Root
      {...(value !== undefined && { value })}
      onValueChange={(v) => {
        markDirty?.();
        onChange(v);
      }}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      // A thin themed scrollbar rather than the platform default, which is wide
      // enough on Windows to crowd the rows it sits beside. `pr-2` keeps the
      // row borders off it.
      className={`flex w-full flex-col gap-3 ${maxHeight === undefined ? "" : "overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-border-strong [&::-webkit-scrollbar-thumb:hover]:bg-mid-gray [&::-webkit-scrollbar-track]:rounded-[3px] [&::-webkit-scrollbar-track]:bg-secondary"}`}
      {...(maxHeight !== undefined && { style: { maxHeight } })}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled ?? false}
          // `group` so the chip can fill in when the row is picked. The text
          // colour deliberately doesn't change (unlike ChoiceCards' brand
          // accent) — these rows carry tags and captions that own their own
          // colour, and turning the lot brand flattens that.
          className="group flex w-full shrink-0 cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 data-[state=checked]:border-brand data-[state=checked]:bg-brand-subtle"
        >
          {option.icon === undefined ? (
            option.content
          ) : (
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-light-background transition-colors group-data-[state=checked]:bg-secondary">
                <Icon
                  icon={option.icon}
                  weight="fill"
                  size="xl"
                  color="text-blue-text"
                />
              </span>
              {option.content}
            </span>
          )}
          <RadioMark checked={value === option.value} />
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
